/**
 * getApiErrorMessage — turn any API/network error into ONE short, readable
 * sentence safe to show a user.
 *
 * Ported from octa-frontend so the admin console stops hand-rolling
 * `e?.data?.message || "..."` at each call site. That pattern loses two things
 * every time: the correlation id, and the distinction between an error a user
 * can act on and one they cannot.
 *
 * The correlation id matters MORE here than in the customer app. This is the
 * console support staff work from — the same people who resolve a reference in
 * the audit viewer — and they had no way to read one off their own error.
 *
 * The backend now returns `{ status: 'error', message }` with user-friendly
 * text (see octa-backend appErrorAdapter/errorHandler), but this helper is the
 * single place the frontend decides what to display. It also:
 *   - handles network / timeout failures (no HTTP body),
 *   - reads legacy shapes ({ errors: [...] }, { message: [zodIssues] }),
 *   - falls back to a friendly, caller-supplied message,
 *   - and guards against raw technical strings that slip through, so users
 *     never see a Prisma dump or a stack trace.
 *
 * Usage:
 *   message.error(getApiErrorMessageWithRef(err, 'Could not save the role.'));
 */

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.';

/** Heuristic: does this look like an internal/technical message, not user copy? */
function looksTechnical(message: string): boolean {
  const s = message.toLowerCase();
  return (
    s.includes('prisma') ||
    s.includes('queryengine') ||
    s.includes('econnrefused') ||
    s.includes('etimedout') ||
    s.includes('cannot read propert') ||
    s.includes('is not a function') ||
    s.includes('is not defined') ||
    s.includes('unexpected token') ||
    s.includes('\n    at ') ||
    s.includes('at object.') ||
    message.length > 220
  );
}

/** Friendly copy for a bare HTTP status when no usable body message exists. */
function messageForStatus(status: number): string | undefined {
  switch (status) {
    case 400:
    case 422:
      return 'Some of the information provided is invalid. Please review and try again.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return 'The requested item could not be found.';
    case 409:
      return 'This conflicts with existing data. Please refresh and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'The server ran into a problem. Please try again shortly.';
    default:
      return undefined;
  }
}

function firstUsableString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const s = firstUsableString(typeof item === 'object' && item ? (item as any).message : item);
      if (s) return s;
    }
  }
  return undefined;
}

export function getApiErrorMessage(error: unknown, fallback: string = DEFAULT_FALLBACK): string {
  if (!error) return fallback;
  const e = error as any;

  // 1. RTK Query network-level failures (no HTTP response body).
  if (e.status === 'FETCH_ERROR') {
    return 'Unable to reach the server. Please check your internet connection and try again.';
  }
  if (e.status === 'TIMEOUT_ERROR') {
    return 'The request took too long. Please try again.';
  }

  // 2. Standard backend error body: { status:'error', message } (+ legacy shapes).
  const data = e.data ?? e.response?.data ?? e;
  if (data && typeof data === 'object') {
    const candidate =
      firstUsableString(data.message) ||
      firstUsableString(data.errors) ||
      firstUsableString(data.error) ||
      firstUsableString(data.details);
    if (candidate) {
      return looksTechnical(candidate) ? fallback : candidate;
    }
  } else if (typeof data === 'string' && data.trim()) {
    return looksTechnical(data) ? fallback : data.trim();
  }

  // 3. Fall back to a message derived from the HTTP status code.
  const status = typeof e.status === 'number' ? e.status : e.originalStatus;
  if (typeof status === 'number') {
    const byStatus = messageForStatus(status);
    if (byStatus) return byStatus;
  }

  // 4. SerializedError / plain Error (e.g. thrown inside a thunk).
  if (typeof e.message === 'string' && e.message.trim() && !looksTechnical(e.message)) {
    return e.message.trim();
  }

  return fallback;
}

/**
 * Correlation id for a failed request, when the backend supplied one.
 *
 * Every error response carries `requestId` (and the same value comes back in
 * the `X-Request-Id` response header). It is the thread between what the user
 * saw and what the server recorded: pasted into the admin console's audit
 * viewer it returns exactly the rows that request produced.
 */
export function getApiErrorRequestId(error: unknown): string | undefined {
  if (!error) return undefined;
  const e = error as any;
  const data = e.data ?? e.response?.data ?? e;
  const id = data?.requestId ?? e.requestId;
  return typeof id === 'string' && id.trim() ? id.trim() : undefined;
}

/**
 * The message plus a support reference, but ONLY where the reference helps.
 *
 * A validation error the user can fix ("Password must be at least 8
 * characters") gains nothing from a reference code and reads worse with one.
 * A server-side failure is the opposite case: the message is deliberately
 * vague, the user cannot act on it, and the one useful thing they can do is
 * quote the reference. So this appends only for 5xx / network-class failures.
 *
 * In this console that reference is directly actionable: pasted into global
 * search or the audit viewer it returns the exact request, its audit rows and
 * its error count.
 */
export function getApiErrorMessageWithRef(error: unknown, fallback: string = DEFAULT_FALLBACK): string {
  const message = getApiErrorMessage(error, fallback);
  const e = error as any;
  const status = typeof e?.status === 'number' ? e.status : e?.originalStatus;
  const isServerSide = typeof status === 'number' ? status >= 500 : false;
  if (!isServerSide) return message;

  const requestId = getApiErrorRequestId(error);
  return requestId ? `${message} (ref: ${requestId})` : message;
}

export default getApiErrorMessage;
